import {
  DollarSign,
  TrendingUp,
  FileText,
  Package,
  Tool,
  Shield,
  Calendar,
  Clipboard,
  Truck,
  Star,
} from "react-feather";

/**
 * Module identity for the One Stop tree: one fixed colour + icon per module.
 *
 * The palette is a validated categorical set - it passes the lightness band,
 * chroma floor, adjacent-pair CVD separation and 3:1 contrast checks against
 * both the light and the dark chart surface. Nine chromatic slots is the
 * ceiling that still passes; Vehicles deliberately takes a neutral slate as
 * the tenth ("Other") slot rather than a tenth hue too close to something
 * else. Assignments are FIXED per module and never cycled - the same module
 * is the same colour on every report, whichever modules happen to be present.
 *
 * The module NAME always renders in normal ink beside the colour, so colour is
 * never the only cue (which is also what makes the one adjacent pair that sits
 * in the 6-8 CVD band acceptable here).
 *
 * Light/dark are separate steps from the same hues, not an automatic flip.
 */
export const MODULE_META = {
  SALES: { label: "Sales", light: "#1565C0", dark: "#4B8FD6", Icon: TrendingUp },
  ATTENDANCE: { label: "Attendance & Leave", light: "#EF6C00", dark: "#D2761F", Icon: Calendar },
  GUARDING: { label: "Guarding", light: "#5E35B1", dark: "#8E72D8", Icon: Shield },
  COLLECTIONS: { label: "Collections", light: "#2E9A4E", dark: "#3FA765", Icon: DollarSign },
  QUOTATIONS: { label: "Quotations", light: "#8E24AA", dark: "#B85FC6", Icon: FileText },
  STOCK: { label: "Stock", light: "#8A5000", dark: "#AD8236", Icon: Package },
  TECHNICAL: { label: "Technical Work", light: "#00879B", dark: "#2FA6B6", Icon: Tool },
  PETTYCASH: { label: "Petty Cash", light: "#C2185B", dark: "#D9527F", Icon: Clipboard },
  AMC: { label: "AMC & Equipment", light: "#4E7B00", dark: "#7A9B33", Icon: Star },
  VEHICLES: { label: "Vehicles", light: "#546E7A", dark: "#8A9AA5", Icon: Truck },
  HR: { label: "HR", light: "#546E7A", dark: "#8A9AA5", Icon: Star },
};

const FALLBACK = { label: "Other", light: "#546E7A", dark: "#8A9AA5", Icon: Star };

export const moduleMeta = (moduleCode) => MODULE_META[moduleCode] || FALLBACK;

// The app has no dark theme today, so the light step is what actually renders;
// the dark steps are kept here (already validated) so a future theme switch is
// a one-line change rather than a fresh palette exercise.
export const moduleColor = (moduleCode) => moduleMeta(moduleCode).light;

/**
 * Which modules could carry rows for each master type. Used only to say
 * "no activity in period" for the ones that came back empty - so a reader can
 * tell the difference between "we looked and there was nothing" and "we never
 * looked at that module".
 *
 * These lists mirror sp_app_onestop_rows exactly. If a module is added there,
 * add it here too or it will silently stop being reported as quiet.
 */
export const MODULES_BY_MASTER = {
  CUSTOMER: ["COLLECTIONS", "SALES", "QUOTATIONS", "STOCK", "TECHNICAL", "GUARDING", "ATTENDANCE", "AMC"],
  EMPLOYEE: ["COLLECTIONS", "STOCK", "TECHNICAL", "GUARDING", "ATTENDANCE", "PETTYCASH", "VEHICLES", "HR"],
  PRODUCT: ["STOCK", "QUOTATIONS", "TECHNICAL", "VEHICLES", "AMC"],
  VEHICLE: ["VEHICLES", "PETTYCASH", "STOCK", "HR"],
};
